import type { FC } from 'react'
import { Box, IconButton } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { FFInput } from '~/shared/ui'
import { useState, useEffect, useEffectEvent } from 'react'
import { Shift, isShiftStartTimeInPast, nextErrorText } from './utils'
import { haveChanged } from '~/shared/lib/haveChanged'

interface ShiftEditorProps {
  shift: Shift
  displayError: boolean
  isHistory: boolean
  onChange: (shift: Shift) => void
  onDelete: (shift: Shift) => void
  onError: (error: boolean) => void
}

// Each check's message, or null when it passes.
const shiftProblems = (shift: Shift) => {
  const { lower, upper } = shift.effective_period
  const isNewShiftInPast =
    !!lower && !!shift.start_time && shift.id < 0 && isShiftStartTimeInPast(lower, shift.start_time)
  return {
    lowerEmpty: lower ? null : 'Start empty',
    startEmpty: shift.start_time ? null : 'From empty',
    stopEmpty: shift.stop_time ? null : 'To empty',
    time: shift.start_time > shift.stop_time ? 'From more than To' : null,
    date: upper && lower > upper ? 'Start more than End' : null,
    pastTime: isNewShiftInPast ? 'Cannot schedule shift in the past' : null,
  }
}

const ShiftEditor: FC<ShiftEditorProps> = props => {
  const { shift, displayError } = props
  const [timeErrorText, setTimeErrorText] = useState<string>('')
  const [dateErrorText, setDateErrorText] = useState<string>('')
  const [lowerEmptyErrorText, setLowerEmptyErrorText] = useState<string>('')
  const [startEmptyErrorText, setStartEmptyErrorText] = useState<string>('')
  const [stopEmptyErrorText, setStopEmptyErrorText] = useState<string>('')
  const [pastTimeErrorText, setPastTimeErrorText] = useState<string>('')

  const problems = shiftProblems(shift)
  const hasError = Object.values(problems).some(problem => problem !== null)
  const validationSource = [shift, displayError]
  const [validatedFrom, setValidatedFrom] = useState<unknown[]>()
  if (haveChanged(validatedFrom, validationSource)) {
    setValidatedFrom(validationSource)
    setLowerEmptyErrorText(current => nextErrorText(problems.lowerEmpty, displayError, current))
    setStartEmptyErrorText(current => nextErrorText(problems.startEmpty, displayError, current))
    setStopEmptyErrorText(current => nextErrorText(problems.stopEmpty, displayError, current))
    setTimeErrorText(current => nextErrorText(problems.time, displayError, current))
    setDateErrorText(current => nextErrorText(problems.date, displayError, current))
    setPastTimeErrorText(current => nextErrorText(problems.pastTime, displayError, current))
  }

  // The parent passes `onError` as an inline arrow; reporting through an event keeps the
  // validation keyed on what is validated.
  const reportError = useEffectEvent((error: boolean) => props.onError(error))
  useEffect(() => {
    reportError(hasError)
  }, [shift, displayError, hasError])

  const getMinValue = () => {
    const dtInEst = new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })
    const estDt = new Date(dtInEst)
    const shiftStartTime = shift.start_time.split(':')

    if (
      estDt <
      new Date(
        estDt.getFullYear(),
        estDt.getMonth(),
        estDt.getDate(),
        Number(shiftStartTime[0]),
        Number(shiftStartTime[1]),
      )
    )
      return new Date(estDt.setDate(estDt.getDate() - 1)).toISOString().slice(0, 10)
    else return new Date().toISOString().slice(0, 10)
  }

  return (
    <Box sx={{ marginTop: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box sx={{ marginRight: 1 }}>
          <FFInput
            error={!!timeErrorText || !!startEmptyErrorText || !!pastTimeErrorText}
            variant="outlined"
            onChange={event =>
              props.onChange({
                ...shift,
                start_time: event.target.value,
              })
            }
            value={shift.start_time}
            label="From"
            type="time"
            helperText={startEmptyErrorText || timeErrorText || pastTimeErrorText}
            slotProps={{
              inputLabel: { shrink: true },
              input: {
                inputProps: {
                  readOnly: props.isHistory,
                },
              },
            }}
          />
        </Box>
        <Box sx={{ alignSelf: 'center' }}>–</Box>
        <Box sx={{ marginLeft: 1 }}>
          <FFInput
            error={!!timeErrorText || !!stopEmptyErrorText}
            variant="outlined"
            onChange={event =>
              props.onChange({
                ...shift,
                stop_time: event.target.value,
              })
            }
            value={shift.stop_time}
            label="To"
            type="time"
            helperText={stopEmptyErrorText || timeErrorText}
            slotProps={{
              inputLabel: { shrink: true },
              input: {
                inputProps: {
                  readOnly: props.isHistory,
                },
              },
            }}
          />
        </Box>
        <Box sx={{ marginLeft: 4 }}>
          <FFInput
            error={!!dateErrorText || !!lowerEmptyErrorText || !!pastTimeErrorText}
            variant="outlined"
            onChange={event =>
              props.onChange({
                ...shift,
                effective_period: {
                  lower: event.target.value,
                  upper: shift.effective_period.upper,
                  bounds: shift.effective_period.bounds,
                },
              })
            }
            value={shift.effective_period.lower}
            label="Start On"
            type="date"
            helperText={lowerEmptyErrorText || dateErrorText || pastTimeErrorText}
            slotProps={{
              inputLabel: { shrink: true },
              input: {
                inputProps: {
                  min: new Date().toISOString().slice(0, 10),
                  readOnly: props.isHistory,
                },
              },
            }}
          />
        </Box>
        <Box sx={{ marginLeft: 2 }}>
          <FFInput
            error={!!dateErrorText}
            variant="outlined"
            onChange={event =>
              props.onChange({
                ...shift,
                effective_period: {
                  lower: shift.effective_period.lower,
                  upper: event.target.value,
                  bounds: shift.effective_period.bounds,
                },
              })
            }
            value={shift.effective_period.upper}
            label="End On"
            type="date"
            helperText={dateErrorText}
            slotProps={{
              inputLabel: { shrink: true },
              input: {
                inputProps: {
                  min: getMinValue(),
                  readOnly: props.isHistory ? true : false,
                },
              },
            }}
          />
        </Box>
        <Box sx={{ display: props.isHistory ? 'none' : 'block' }}>
          <IconButton
            onClick={() => props.onDelete(shift)}
            disabled={isShiftStartTimeInPast(shift.effective_period.lower, shift.start_time)}
            sx={{
              '&:hover': {
                backgroundColor: 'transparent',
              },
              borderRadius: 'unset',
            }}
          >
            <DeleteIcon color="inherit" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  )
}

export default ShiftEditor
